import { Profile } from './Profile';
import { PlayerSkins } from './PlayerSkins';
import { Perks } from './Perks';

/**
 * Coin Shop — permanent unlocks purchased with coins earned across runs.
 * Items include: skin/color unlocks, perk unlocks, and permanent stat boosts.
 * Purchased state persisted via Profile (v3).
 */

// Item definition
export class Item {
  readonly id: number;
  readonly name: string;
  readonly description: string;
  readonly cost: number;
  readonly category: number;
  purchased: boolean;

  constructor(id: number, name: string, desc: string, cost: number, category: number) {
    this.id = id;
    this.name = name;
    this.description = desc;
    this.cost = cost;
    this.category = category;
    this.purchased = false;
  }
}

export class CoinShop {

  // Item categories
  static readonly CAT_UPGRADE = 0;
  static readonly CAT_SKIN = 1;
  static readonly CAT_COLOR = 2;
  static readonly CAT_PERK = 3;
  static readonly CAT_NAMES: string[] = ["Permanent Upgrades", "Skin Shapes", "Color Palettes", "Perk Unlocks"];

  // ===== Item definitions =====
  // Permanent upgrades (0-6)
  // Skin shapes (7-11) — unlock shapes that also have level requirements
  // Color palettes (12-18) — unlock colors
  // Perk unlocks (19-24) — unlock perks early

  private static readonly ITEMS: Item[] = [
    // --- Permanent upgrades ---
    new Item(0,  "Health Boost I",     "+10 permanent starting health",            200,  CoinShop.CAT_UPGRADE),
    new Item(1,  "Health Boost II",    "+10 more starting health (total +20)",     500,  CoinShop.CAT_UPGRADE),
    new Item(2,  "Health Boost III",   "+10 more starting health (total +30)",     1000, CoinShop.CAT_UPGRADE),
    new Item(3,  "Speed Boost",        "+1 permanent starting speed",              600,  CoinShop.CAT_UPGRADE),
    new Item(4,  "Extra Shield",       "Start with 2 shield charges",              800,  CoinShop.CAT_UPGRADE),
    new Item(5,  "Slow-Mo Reserve",    "+1 starting slow-motion charge",           700,  CoinShop.CAT_UPGRADE),
    new Item(6,  "Coin Magnet",        "Earn 25% more coins per run",              1500, CoinShop.CAT_UPGRADE),

    // --- Skin shapes ---
    new Item(7,  "Circle Shape",       "Unlock the Circle player shape",           150,  CoinShop.CAT_SKIN),
    new Item(8,  "Triangle Shape",     "Unlock the Triangle player shape",         300,  CoinShop.CAT_SKIN),
    new Item(9,  "Diamond Shape",      "Unlock the Diamond player shape",          250,  CoinShop.CAT_SKIN),
    new Item(10, "Star Shape",         "Unlock the Star player shape",             600,  CoinShop.CAT_SKIN),
    new Item(11, "Hexagon Shape",      "Unlock the Hexagon player shape",          500,  CoinShop.CAT_SKIN),

    // --- Color palettes ---
    new Item(12, "Neon Palette",       "Unlock the Neon teal color",               100,  CoinShop.CAT_COLOR),
    new Item(13, "Ember Palette",      "Unlock the Ember orange color",            200,  CoinShop.CAT_COLOR),
    new Item(14, "Royal Palette",      "Unlock the Royal purple color",            350,  CoinShop.CAT_COLOR),
    new Item(15, "Ghost Palette",      "Unlock the Ghost silver color",            500,  CoinShop.CAT_COLOR),
    new Item(16, "Golden Palette",     "Unlock the Golden color",                  800,  CoinShop.CAT_COLOR),
    new Item(17, "Crimson Palette",    "Unlock the Crimson red color",             600,  CoinShop.CAT_COLOR),
    new Item(18, "Lime Palette",       "Unlock the Lime green color",              400,  CoinShop.CAT_COLOR),

    // --- Perk unlocks ---
    new Item(19, "Thick Skin Perk",    "Unlock the Thick Skin perk",               300,  CoinShop.CAT_PERK),
    new Item(20, "Fortified Perk",     "Unlock the Fortified perk",                400,  CoinShop.CAT_PERK),
    new Item(21, "Adrenaline Perk",    "Unlock the Adrenaline perk",               500,  CoinShop.CAT_PERK),
    new Item(22, "Glass Cannon Perk",  "Unlock the Glass Cannon perk",             600,  CoinShop.CAT_PERK),
    new Item(23, "Second Wind Perk",   "Unlock the Second Wind perk",              700,  CoinShop.CAT_PERK),
    new Item(24, "Slow Starter Perk",  "Unlock the Slow Starter perk",             500,  CoinShop.CAT_PERK),
  ];

  static readonly ITEM_COUNT: number = CoinShop.ITEMS.length;

  // ===== Queries =====

  static getItem(id: number): Item | null { return (id >= 0 && id < CoinShop.ITEM_COUNT) ? CoinShop.ITEMS[id] : null; }
  static getItemCount(): number { return CoinShop.ITEM_COUNT; }
  static isPurchased(id: number): boolean { return (id >= 0 && id < CoinShop.ITEM_COUNT) && CoinShop.ITEMS[id].purchased; }

  static canAfford(id: number): boolean {
    return id >= 0 && id < CoinShop.ITEM_COUNT && Number(Profile.getCoins()) >= CoinShop.ITEMS[id].cost;
  }

  // ===== Purchase =====

  static purchase(id: number): boolean {
    if (id < 0 || id >= CoinShop.ITEM_COUNT) return false;
    const item = CoinShop.ITEMS[id];
    if (item.purchased) return false;
    if (!Profile.spendCoins(BigInt(item.cost))) return false;
    item.purchased = true;
    Profile.saveSkinSelection(); // triggers full save
    return true;
  }

  // ===== Effect queries for gameplay =====

  /** Permanent starting health bonus from shop */
  static getPermHealthBonus(): number {
    let bonus = 0;
    if (CoinShop.ITEMS[0].purchased) bonus += 10;
    if (CoinShop.ITEMS[1].purchased) bonus += 10;
    if (CoinShop.ITEMS[2].purchased) bonus += 10;
    return bonus;
  }

  /** Permanent starting speed bonus from shop */
  static getPermSpeedBonus(): number {
    return CoinShop.ITEMS[3].purchased ? 1 : 0;
  }

  /** Extra shield charges from shop */
  static getExtraShieldCharges(): number {
    return CoinShop.ITEMS[4].purchased ? 1 : 0;
  }

  /** Extra slow-mo charges from shop */
  static getExtraSlowmoCharges(): number {
    return CoinShop.ITEMS[5].purchased ? 1 : 0;
  }

  /** Coin earning multiplier */
  static getCoinMultiplier(): number {
    return CoinShop.ITEMS[6].purchased ? 1.25 : 1;
  }

  /** Check if a skin shape is unlocked via shop (alternative to level unlock) */
  static isShapeUnlockedByShop(shape: number): boolean {
    switch (shape) {
      case PlayerSkins.SHAPE_CIRCLE:   return CoinShop.ITEMS[7].purchased;
      case PlayerSkins.SHAPE_TRIANGLE: return CoinShop.ITEMS[8].purchased;
      case PlayerSkins.SHAPE_DIAMOND:  return CoinShop.ITEMS[9].purchased;
      case PlayerSkins.SHAPE_STAR:     return CoinShop.ITEMS[10].purchased;
      case PlayerSkins.SHAPE_HEXAGON:  return CoinShop.ITEMS[11].purchased;
      default: return false;
    }
  }

  /** Check if a color is unlocked via shop */
  static isColorUnlockedByShop(color: number): boolean {
    switch (color) {
      case PlayerSkins.COLOR_NEON:     return CoinShop.ITEMS[12].purchased;
      case PlayerSkins.COLOR_EMBER:    return CoinShop.ITEMS[13].purchased;
      case PlayerSkins.COLOR_ROYAL:    return CoinShop.ITEMS[14].purchased;
      case PlayerSkins.COLOR_GHOST:    return CoinShop.ITEMS[15].purchased;
      case PlayerSkins.COLOR_GOLDEN:   return CoinShop.ITEMS[16].purchased;
      case PlayerSkins.COLOR_CRIMSON:  return CoinShop.ITEMS[17].purchased;
      case PlayerSkins.COLOR_LIME:     return CoinShop.ITEMS[18].purchased;
      default: return false;
    }
  }

  /** Check if a perk is unlocked via shop */
  static isPerkUnlockedByShop(perk: number): boolean {
    switch (perk) {
      case Perks.THICK_SKIN:   return CoinShop.ITEMS[19].purchased;
      case Perks.FORTIFIED:    return CoinShop.ITEMS[20].purchased;
      case Perks.ADRENALINE:   return CoinShop.ITEMS[21].purchased;
      case Perks.GLASS_CANNON: return CoinShop.ITEMS[22].purchased;
      case Perks.SECOND_WIND:  return CoinShop.ITEMS[23].purchased;
      case Perks.SLOW_STARTER: return CoinShop.ITEMS[24].purchased;
      default: return false;
    }
  }

  // ===== Persistence helpers (called by Profile) =====

  static loadPurchased(data: boolean[]): void {
    const count = Math.min(data.length, CoinShop.ITEM_COUNT);
    for (let i = 0; i < count; i++) {
      CoinShop.ITEMS[i].purchased = data[i];
    }
  }

  static resetAll(): void {
    for (const item of CoinShop.ITEMS) item.purchased = false;
  }
}
